import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { API, auth, err } from '../invoices/invoiceApi';
import { TestSendDialog } from './TestSendDialog';

const USE_LABELS = { name: 'Business name', phone: 'Phone', email: 'Email', address: 'Address', website: 'Website', logo: 'Logo' };

export const EmailPreviewPane = ({ token, meta, me, canSend }) => {
  const [p, setP] = useState(null);
  const [to, setTo] = useState(me || '');
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    setP(null);
    axios.get(`${API}/admin/email-preview/${encodeURIComponent(meta.key)}`, auth(token)).then((r) => setP(r.data)).catch((e) => setP({ error: err(e, 'Could not render') }));
  }, [token, meta.key]);
  const openPdf = () => {
    const w = window.open('', '_blank');
    axios.get(`${API}/admin/email-preview/${encodeURIComponent(meta.key)}/pdf`, { ...auth(token), responseType: 'blob' })
      .then((r) => { w.location.href = URL.createObjectURL(r.data); })
      .catch(() => { w.close(); toast.error('Could not build the sample PDF'); });
  };
  const validTo = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim());
  return (
    <section className="bg-white border rounded-lg p-4 space-y-3 min-w-0" data-testid="email-preview-pane">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-neutral-500" data-testid="email-preview-trigger">Sent when: {meta.trigger}</p>
          <p className="font-semibold truncate" data-testid="email-preview-subject">{p?.subject || (p ? '' : 'Loading…')}</p>
        </div>
      </div>
      {p?.uses && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="email-preview-uses">
          <span className="text-neutral-500">From Company Profile:</span>
          {p.uses.length ? p.uses.map((u) => <span key={u} className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{USE_LABELS[u]}</span>) : <span className="text-neutral-400">none in this email</span>}
          {meta.pdf_logo && <span className="px-2 py-0.5 rounded-full bg-neutral-100 border">Logo on attached PDF</span>}
        </div>
      )}
      {p?.error && <p className="text-sm text-red-600" data-testid="email-preview-render-error">{p.error}</p>}
      {!p && <div className="h-96 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>}
      {p?.html && <iframe title="Email preview" sandbox="" srcDoc={p.html} className="w-full h-[60vh] border rounded bg-neutral-50" data-testid="email-preview-frame" />}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@yourcompany.com" className="max-w-xs" data-testid="email-preview-test-to" />
        <Button size="sm" disabled={!canSend || !validTo || !p?.html} onClick={() => setConfirming(true)} data-testid="email-preview-test-open"><Send className="w-4 h-4 mr-1" />Send test</Button>
        {meta.pdf_logo && <Button size="sm" variant="outline" onClick={openPdf} data-testid="email-preview-view-pdf"><FileText className="w-4 h-4 mr-1" />View sample PDF</Button>}
        <p className="text-xs text-neutral-500">{canSend ? 'Only to you or your team. Customer addresses are blocked.' : 'Activate a sender in Email Settings to send tests.'}</p>
      </div>
      <TestSendDialog open={confirming} onOpenChange={setConfirming} token={token} meta={meta} subject={p?.subject} to={to.trim()} />
    </section>
  );
};
